import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from './ui/select';
import { formatCurrency } from '../utils/formatters';
import { Account } from '../types';
import api from '../services/api';

interface CSVImportModalProps {
  open: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  accounts: Account[];
}

type Step = 'upload' | 'importing' | 'results';

interface FuzzyMatch {
  incoming: { date: string; amount: number; description: string | null; merchant: string | null };
  existing: { id: number; date: string; amount: number; description: string | null };
  confidence: number;
  reason: string;
}

interface ExactDuplicate {
  incoming: { date: string; amount: number; description: string | null; merchant: string | null };
  existing: { id: number; date: string; amount: number; description: string | null };
}

interface ImportResult {
  inserted_count: number;
  exact_duplicate_count: number;
  exact_duplicates: ExactDuplicate[];
  fuzzy_matches: FuzzyMatch[];
  linked_account_id: number;
}

// Import-context account order: bank statements and credit cards first
const IMPORT_TYPE_ORDER = [
  { key: 'checking',       label: '🏦 Chequing & Bank Accounts' },
  { key: 'credit_card',    label: '💳 Credit Cards' },
  { key: 'savings',        label: '💰 Savings Accounts' },
  { key: 'investment',     label: '📈 Investment Accounts' },
  { key: 'loan',           label: '🏠 Loans & Mortgages' },
  { key: 'line_of_credit', label: '📋 Lines of Credit' },
  { key: 'other',          label: '📁 Other' },
];

function groupAccountsForImport(accounts: Account[]) {
  const groups: Record<string, Account[]> = {};
  for (const a of accounts) {
    const key = a.type || 'other';
    if (!groups[key]) groups[key] = [];
    groups[key].push(a);
  }
  return groups;
}

function detectFormat(filename: string, firstLine: string): { label: string; institutionKey: string | null } {
  const combined = (filename + ' ' + firstLine).toLowerCase();
  if (combined.includes('td') || combined.includes('toronto-dominion')) return { label: 'TD Bank', institutionKey: 'td' };
  if (combined.includes('scotia') || combined.includes('scotiabank'))    return { label: 'ScotiaBank', institutionKey: 'scotia' };
  if (combined.includes('rbc') || combined.includes('royal bank'))       return { label: 'RBC', institutionKey: 'rbc' };
  if (combined.includes('cibc'))                                          return { label: 'CIBC', institutionKey: 'cibc' };
  if (combined.includes('bmo') || combined.includes('bank of montreal')) return { label: 'BMO', institutionKey: 'bmo' };
  return { label: 'Unknown format', institutionKey: null };
}

const UploadIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

const FileIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
  </svg>
);

const CSVImportModal: React.FC<CSVImportModalProps> = ({ open, onClose, onImportComplete, accounts }) => {
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [accountId, setAccountId] = useState<string>('');
  const [detectedFormat, setDetectedFormat] = useState<string>('');
  const [formatKnown, setFormatKnown] = useState(false);
  const [csvPreview, setCsvPreview] = useState<string[][]>([]);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [overrideSelections, setOverrideSelections] = useState<Set<number>>(new Set());
  const [exactOverrideSelections, setExactOverrideSelections] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [importingOverrides, setImportingOverrides] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setStep('upload');
      setFile(null);
      setAccountId('');
      setDetectedFormat('');
      setFormatKnown(false);
      setCsvPreview([]);
      setResult(null);
      setOverrideSelections(new Set());
      setExactOverrideSelections(new Set());
      setError(null);
      setIsDragging(false);
      setImportingOverrides(false);
    }
  }, [open]);

  function handleFile(f: File) {
    setFile(f);
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      const firstLine = lines[0] || '';
      const fmt = detectFormat(f.name, firstLine);
      setDetectedFormat(fmt.label);
      setFormatKnown(fmt.institutionKey !== null);
      if (fmt.institutionKey) {
        const match = accounts.find(a => a.institution?.toLowerCase().includes(fmt.institutionKey!));
        if (match) setAccountId(String(match.id));
      }
      const preview = lines.slice(0, 6).map(line =>
        line.split(',').map(cell => cell.trim().replace(/^"|"$/g, ''))
      );
      setCsvPreview(preview);
    };
    reader.readAsText(f);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped && (dropped.name.endsWith('.csv') || dropped.type === 'text/csv')) {
      handleFile(dropped);
    } else {
      setError('Please drop a CSV file.');
    }
  }

  async function handleImport() {
    if (!file || !accountId) return;
    setError(null);
    setStep('importing');
    try {
      const res = await api.transactions.importTransactionsCSV({ manual_account_id: Number(accountId), file });
      setResult(res);
      setStep('results');
    } catch (e: any) {
      setError(e?.message || 'Failed to import CSV.');
      setStep('upload');
    }
  }

  async function handleImportOverrides() {
    if (!result) return;
    const totalSelected = overrideSelections.size + exactOverrideSelections.size;
    if (totalSelected === 0) return;
    setImportingOverrides(true);
    try {
      const fuzzySelected = Array.from(overrideSelections).map(i => result.fuzzy_matches[i].incoming);
      const exactSelected = Array.from(exactOverrideSelections).map(i => result.exact_duplicates[i].incoming);
      const all = [...fuzzySelected, ...exactSelected];
      await api.transactions.importOverrides({ linked_account_id: result.linked_account_id, transactions: all });
      onImportComplete();
    } catch (e: any) {
      setError(e?.message || 'Failed to import selected transactions.');
      setImportingOverrides(false);
    }
  }

  const selectedCount = overrideSelections.size + exactOverrideSelections.size;
  const grouped = groupAccountsForImport(accounts);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            {step === 'upload' && <><span className="text-purple-400">↑</span> Import CSV</>}
            {step === 'importing' && <>Importing…</>}
            {step === 'results' && <>Import Results</>}
          </DialogTitle>
        </DialogHeader>

        {/* ── Upload Screen ── */}
        {step === 'upload' && (
          <div className="space-y-4">

            {/* Drop zone */}
            <div
              className={`relative rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-all duration-150 ${
                isDragging
                  ? 'border-purple-400 bg-purple-500/10'
                  : file
                  ? 'border-green-500/40 bg-green-500/5 hover:bg-green-500/8'
                  : 'border-white/15 bg-white/[0.02] hover:border-purple-500/40 hover:bg-purple-500/5'
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
              />
              {file ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="text-green-400 flex items-center gap-2">
                    <FileIcon />
                    <span className="font-medium text-slate-200">{file.name}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {detectedFormat && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        formatKnown
                          ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                          : 'bg-slate-500/20 text-slate-400 border border-white/10'
                      }`}>
                        {formatKnown ? '✓ ' : ''}{detectedFormat}
                      </span>
                    )}
                    <span className="text-xs text-slate-500">Click or drop to replace</span>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <div className={`transition-colors ${isDragging ? 'text-purple-400' : 'text-slate-500'}`}>
                    <UploadIcon />
                  </div>
                  <div>
                    <div className="text-slate-300 text-sm font-medium">Drop a CSV file here</div>
                    <div className="text-slate-500 text-xs mt-0.5">or <span className="text-purple-400 underline underline-offset-2">click to browse</span></div>
                  </div>
                  <div className="text-slate-600 text-xs">TD · ScotiaBank · RBC · CIBC · BMO · generic CSV</div>
                </div>
              )}
            </div>

            {/* CSV Preview */}
            {csvPreview.length > 0 && (
              <div>
                <div className="text-xs text-slate-500 mb-1.5 font-semibold uppercase tracking-wider">Preview</div>
                <div className="overflow-auto rounded-lg border border-white/10 max-h-36">
                  <table className="w-full text-xs">
                    <tbody>
                      {csvPreview.map((row, ri) => (
                        <tr key={ri} className={ri === 0 ? 'bg-purple-500/10 text-purple-200 font-medium' : 'text-slate-300 even:bg-white/[0.02]'}>
                          {row.map((cell, ci) => (
                            <td key={ci} className="px-2.5 py-1.5 border-b border-white/5 truncate max-w-[140px]">{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Account selector */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                Import into account
                {accountId && <span className="ml-2 text-xs text-purple-400 font-normal">✓ auto-selected</span>}
              </label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose an account…" />
                </SelectTrigger>
                <SelectContent>
                  {IMPORT_TYPE_ORDER.map(({ key, label }) => {
                    const group = grouped[key] || [];
                    if (group.length === 0) return null;
                    return (
                      <SelectGroup key={key}>
                        <SelectLabel>{label}</SelectLabel>
                        {group.map(a => (
                          <SelectItem key={a.id} value={String(a.id)}>
                            {a.institution ? `${a.institution} — ` : ''}{a.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    );
                  })}
                  {/* Ungrouped types not in the predefined list */}
                  {Object.entries(grouped).map(([type, accts]) => {
                    if (IMPORT_TYPE_ORDER.some(t => t.key === type)) return null;
                    return (
                      <SelectGroup key={type}>
                        <SelectLabel>📁 {type}</SelectLabel>
                        {accts.map(a => (
                          <SelectItem key={a.id} value={String(a.id)}>
                            {a.institution ? `${a.institution} — ` : ''}{a.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Help — collapsed by default */}
            <details className="rounded-lg border border-white/10 overflow-hidden text-sm">
              <summary className="px-3 py-2 cursor-pointer text-slate-500 select-none hover:text-slate-400 transition-colors">
                Supported formats &amp; tips
              </summary>
              <div className="px-3 pb-3 pt-1 text-slate-500 space-y-1 text-xs border-t border-white/10">
                <p><strong className="text-slate-400">Supported:</strong> ScotiaBank Visa, TD Bank, RBC, CIBC, BMO, and any CSV with Date + Amount columns.</p>
                <p><strong className="text-slate-400">Date formats:</strong> MM/DD/YYYY · DD/MM/YYYY · YYYY-MM-DD</p>
                <p><strong className="text-slate-400">Amount formats:</strong> $123.45 · 123.45 · (123.45) · -123.45</p>
                <p>If auto-detection fails, rename columns to: Date, Description, Amount.</p>
              </div>
            </details>

            {error && (
              <div className="text-red-400 text-sm rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2">{error}</div>
            )}
          </div>
        )}

        {/* ── Importing Screen ── */}
        {step === 'importing' && (
          <div className="flex flex-col items-center justify-center py-14 gap-4">
            <div className="w-9 h-9 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-400">Importing transactions…</span>
          </div>
        )}

        {/* ── Results Screen ── */}
        {step === 'results' && result && (
          <div className="space-y-3">

            <div className="flex items-center gap-2.5 rounded-lg p-3.5 bg-green-500/10 border border-green-500/25">
              <span className="text-green-400 text-xl leading-none">✓</span>
              <div className="flex-1">
                <span className="font-semibold text-green-400">{result.inserted_count} transaction{result.inserted_count !== 1 ? 's' : ''} imported</span>
                {result.exact_duplicate_count === 0 && result.fuzzy_matches.length === 0 && (
                  <div className="text-xs text-green-600 mt-0.5">All clean — no duplicates found</div>
                )}
              </div>
              {result.inserted_count > 0 && (
                <button
                  onClick={onImportComplete}
                  style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary-color, #7c3aed)', cursor: 'pointer', fontSize: '12px', fontWeight: 500, textDecoration: 'underline', flexShrink: 0 }}
                >
                  Categorize now →
                </button>
              )}
            </div>

            {result.exact_duplicate_count > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-400">
                    ⏭ {result.exact_duplicate_count} already existed — skipped
                  </span>
                  <span className="text-xs text-slate-500">Check any to import anyway</span>
                </div>
                <div className="space-y-2">
                  {(result.exact_duplicates ?? []).map((dup, i) => (
                    <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] overflow-hidden">
                      <div className="grid grid-cols-2 divide-x divide-white/10">
                        <div className="p-3">
                          <div className="text-[10px] text-slate-500 mb-1 font-semibold uppercase tracking-widest">Incoming</div>
                          <div className="text-sm font-medium text-slate-200 truncate">{dup.incoming.description || dup.incoming.merchant || '—'}</div>
                          <div className="text-xs text-slate-400 mt-1">{dup.incoming.date} · {formatCurrency(Math.abs(dup.incoming.amount), 'CAD')}</div>
                        </div>
                        <div className="p-3">
                          <div className="text-[10px] text-slate-500 mb-1 font-semibold uppercase tracking-widest">
                            Existing <span className="text-slate-400 normal-case">(exact match)</span>
                          </div>
                          <div className="text-sm font-medium text-slate-200 truncate">{dup.existing.description || '—'}</div>
                          <div className="text-xs text-slate-400 mt-1">{dup.existing.date} · {formatCurrency(Math.abs(dup.existing.amount), 'CAD')}</div>
                        </div>
                      </div>
                      <div className="px-3 py-2 border-t border-white/10 flex items-center gap-2 bg-white/[0.02]">
                        <input
                          type="checkbox"
                          id={`exact-override-${i}`}
                          checked={exactOverrideSelections.has(i)}
                          onChange={e => {
                            setExactOverrideSelections(prev => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(i); else next.delete(i);
                              return next;
                            });
                          }}
                          className="w-4 h-4 rounded"
                        />
                        <label htmlFor={`exact-override-${i}`} className="text-xs text-slate-400 cursor-pointer select-none">
                          Import anyway
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.fuzzy_matches.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-amber-400">
                    ⚠ {result.fuzzy_matches.length} possible duplicate{result.fuzzy_matches.length !== 1 ? 's' : ''} — review below
                  </span>
                  <span className="text-xs text-slate-500">Check any to import anyway</span>
                </div>
                <div className="space-y-2">
                  {result.fuzzy_matches.map((match, i) => (
                    <div key={i} className="rounded-lg border border-amber-500/20 bg-amber-500/5 overflow-hidden">
                      <div className="grid grid-cols-2 divide-x divide-white/10">
                        <div className="p-3">
                          <div className="text-[10px] text-slate-500 mb-1 font-semibold uppercase tracking-widest">Incoming</div>
                          <div className="text-sm font-medium text-slate-200 truncate">{match.incoming.description || match.incoming.merchant || '—'}</div>
                          <div className="text-xs text-slate-400 mt-1">{match.incoming.date} · {formatCurrency(Math.abs(match.incoming.amount), 'CAD')}</div>
                        </div>
                        <div className="p-3">
                          <div className="text-[10px] text-slate-500 mb-1 font-semibold uppercase tracking-widest">
                            Existing <span className="text-amber-400 normal-case">({Math.round(match.confidence * 100)}% match)</span>
                          </div>
                          <div className="text-sm font-medium text-slate-200 truncate">{match.existing.description || '—'}</div>
                          <div className="text-xs text-slate-400 mt-1">{match.existing.date} · {formatCurrency(Math.abs(match.existing.amount), 'CAD')}</div>
                        </div>
                      </div>
                      <div className="px-3 py-2 border-t border-white/10 flex items-center gap-2 bg-white/[0.02]">
                        <input
                          type="checkbox"
                          id={`override-${i}`}
                          checked={overrideSelections.has(i)}
                          onChange={e => {
                            setOverrideSelections(prev => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(i); else next.delete(i);
                              return next;
                            });
                          }}
                        />
                        <label htmlFor={`override-${i}`} className="text-xs text-slate-400 cursor-pointer">Import this one anyway</label>
                        <span className="ml-auto text-xs text-slate-600 italic">{match.reason}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {error && (
              <div className="text-red-400 text-sm rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2">{error}</div>
            )}
          </div>
        )}

        {/* ── Footer ── */}
        <DialogFooter>
          {step === 'upload' && (
            <>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={handleImport} disabled={!file || !accountId}>
                Import
              </Button>
            </>
          )}
          {step === 'results' && result && (
            selectedCount > 0 ? (
              <>
                <Button variant="ghost" onClick={onImportComplete} disabled={importingOverrides}>
                  Done without importing
                </Button>
                <Button onClick={handleImportOverrides} disabled={importingOverrides}>
                  {importingOverrides ? 'Importing…' : `Import ${selectedCount} selected`}
                </Button>
              </>
            ) : (
              <Button onClick={onImportComplete}>Done</Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CSVImportModal;
